import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import MarketTickerBar from './components/MarketTickerBar';

// Pages
//import Homepage from './pages/Homepage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreateAnalysis from './pages/CreateAnalysis';
import AnalysisDetail from './pages/AnalysisDetail';
import SectionViewer from './pages/SectionViewer';
import GoogleCallback from './pages/GoogleCallback';
import VerifyEmail from './pages/VerifyEmail';
import RegistrationSuccess from './pages/RegistrationSuccess';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AccountSettings from './pages/AccountSettings';
import Bookmarks from './pages/Bookmarks';
import Datasets from "./pages/Datasets";
import DatasetExplorer from "./pages/DatasetExplorer";
import DatasetsAdmin from "./pages/DatasetAdmin";
import CreateDataset from "./pages/CreateDataset";
import Datahubs from './pages/Datahubs';
import DatahubBuilder from './pages/DatahubBuilder';
import TreasuryExplorer from './pages/TreasuryExplorer';
import EconomicCalendar from './pages/EconomicCalendar';
import Research from './pages/Research';
import EconomicSnapshot from './pages/EconomicSnapshot';
import BLSLayout from './layouts/BLSLayout';
import BEALayout from './layouts/BEALayout';
import CUExplorer from './pages/bls/CUExplorer';
import LNExplorer from './pages/bls/LNExplorer';
import LAExplorer from './pages/bls/LAExplorer';
import CEExplorer from './pages/bls/CEExplorer';
import PCExplorer from './pages/bls/PCExplorer';
import WPExplorer from './pages/bls/WPExplorer';
import APExplorer from './pages/bls/APExplorer';
import CWExplorer from './pages/bls/CWExplorer';
import SMExplorer from './pages/bls/SMExplorer';
import JTExplorer from './pages/bls/JTExplorer';
import OEExplorer from './pages/bls/OEExplorer';
import ECExplorer from './pages/bls/ECExplorer';
import PRExplorer from './pages/bls/PRExplorer';
import TUExplorer from './pages/bls/TUExplorer';
import IPExplorer from './pages/bls/IPExplorer';
import SUExplorer from './pages/bls/SUExplorer';
import BDExplorer from './pages/bls/BDExplorer';
import EIExplorer from './pages/bls/EIExplorer';

// FRED Explorer Pages
import ClaimsExplorer from './pages/ClaimsExplorer';
import YieldCurveExplorer from './pages/YieldCurveExplorer';
import FedFundsExplorer from './pages/FedFundsExplorer';
import SentimentExplorer from './pages/SentimentExplorer';
import LeadingIndexExplorer from './pages/LeadingIndexExplorer';
import HousingExplorer from './pages/HousingExplorer';
import FREDCalendarExplorer from './pages/FREDCalendarExplorer';
import FREDReleasePage from './pages/FREDReleasePage';
import FREDSeriesPage from './pages/FREDSeriesPage';

// BEA Explorer Pages
import NIPAExplorer from './pages/NIPAExplorer';
import RegionalExplorer from './pages/RegionalExplorer';
import GDPbyIndustryExplorer from './pages/GDPbyIndustryExplorer';
import ITAExplorer from './pages/ITAExplorer';
import FixedAssetsExplorer from './pages/FixedAssetsExplorer';

// Stocks Module
import StocksPortal from './pages/stocks/StocksPortal';
import Screener from './pages/stocks/Screener';
import StockDetail from './pages/stocks/StockDetail';
import SnapshotPreview from './pages/stocks/SnapshotPreview';
import ClassificationPreview from './pages/stocks/ClassificationPreview';
import MarketContext from './pages/stocks/MarketContext';
import StrategiesHub from './pages/stocks/StrategiesHub';
import StockNewsSection from './pages/stocks/StockNewsSection';

// Articles Module (AI-Generated Reports)
import { ArticlesPortal, ArticleViewer, CategoryList } from './pages/articles';

// Reports Module (Static HTML Reports)
import { ReportsPortal, CategoryList as ReportsCategoryList, SectionList as ReportsSectionList } from './pages/reports';

// News Module (Unified News Portal)
import { NewsPortal, SectionList as NewsSectionList } from './pages/news';

// Trading Module (Signal-based study articles)
import { TradingPortal, TradingSectionList } from './pages/trading';

// History Module (Evergreen financial history articles)
import { HistoryPortal, HistorySectionList, CollectionPage as HistoryCollectionPage } from './pages/history';

// Create a client for react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Conditionally render MarketTickerBar on stocks routes
function ConditionalMarketTicker() {
  const location = useLocation();
  const isStocksRoute = location.pathname.startsWith('/stocks');

  if (!isStocksRoute) return null;
  return <MarketTickerBar />;
}

function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <Router>
          <ConditionalMarketTicker />
          <Header />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Navigate to="/news" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analysis/create"
              element={
                <ProtectedRoute>
                  <CreateAnalysis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analysis/:id"
              element={
                <ProtectedRoute>
                  <AnalysisDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analysis/:id/section/:sectionNum"
              element={
                <ProtectedRoute>
                  <SectionViewer />
                </ProtectedRoute>
              }
            />
            {/* Protected Dataset module */}
            <Route
              path="/datasets"
              element={
                <ProtectedRoute>
                  <Datasets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasets/:id"
              element={
                <ProtectedRoute>
                  <DatasetExplorer />
                </ProtectedRoute>
              }
            />

            {/* Optional: role-guarded admin/tools */}
            <Route
              path="/admin/datasets"
              element={
                <ProtectedRoute roles={['admin']}>
                  <DatasetsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasets/new"
              element={
                <ProtectedRoute>
                  <CreateDataset />
                </ProtectedRoute>
              }
            />
            <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
            <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
            <Route path="/datahubs" element={<ProtectedRoute><Datahubs /></ProtectedRoute>} />
            <Route path="/datahubs/:id" element={<ProtectedRoute><DatahubBuilder /></ProtectedRoute>} />

            {/* Research Module - Public Access */}
            <Route path="/research" element={<Research />} />
            <Route path="/research/economic-snapshot" element={<EconomicSnapshot />} />
            <Route path="/research/treasury" element={<TreasuryExplorer />} />
            <Route path="/research/claims" element={<ClaimsExplorer />} />
            <Route path="/research/fed-funds" element={<FedFundsExplorer />} />
            <Route path="/research/sentiment" element={<SentimentExplorer />} />
            <Route path="/research/leading" element={<LeadingIndexExplorer />} />
            <Route path="/research/housing" element={<HousingExplorer />} />
            <Route path="/research/yield-curve" element={<YieldCurveExplorer />} />
            <Route path="/research/calendar" element={<EconomicCalendar />} />
            <Route path="/research/fred-calendar" element={<FREDCalendarExplorer />} />
            <Route path="/research/fred-calendar/release/:releaseId" element={<FREDReleasePage />} />
            <Route path="/research/fred-calendar/series/:seriesId" element={<FREDSeriesPage />} />

            {/* BEA Research Module - with layout */}
            <Route path="/research/bea" element={<BEALayout />}>
              <Route path="nipa" element={<NIPAExplorer />} />
              <Route path="regional" element={<RegionalExplorer />} />
              <Route path="gdpbyindustry" element={<GDPbyIndustryExplorer />} />
              <Route path="ita" element={<ITAExplorer />} />
              <Route path="fixedassets" element={<FixedAssetsExplorer />} />
            </Route>

            {/* BLS Research Module - with layout */}
            <Route path="/research/bls" element={<BLSLayout />}>
              <Route path="cu" element={<CUExplorer />} />
              <Route path="ln" element={<LNExplorer />} />
              <Route path="la" element={<LAExplorer />} />
              <Route path="ce" element={<CEExplorer />} />
              <Route path="pc" element={<PCExplorer />} />
              <Route path="wp" element={<WPExplorer />} />
              <Route path="ap" element={<APExplorer />} />
              <Route path="cw" element={<CWExplorer />} />
              <Route path="sm" element={<SMExplorer />} />
              <Route path="jt" element={<JTExplorer />} />
              <Route path="oe" element={<OEExplorer />} />
              <Route path="ec" element={<ECExplorer />} />
              <Route path="pr" element={<PRExplorer />} />
              <Route path="tu" element={<TUExplorer />} />
              <Route path="ip" element={<IPExplorer />} />
              <Route path="su" element={<SUExplorer />} />
              <Route path="bd" element={<BDExplorer />} />
              <Route path="ei" element={<EIExplorer />} />
            </Route>

            {/* Stocks Module - Public Access */}
            <Route path="/stocks" element={<StocksPortal />} />
            <Route path="/stocks/screener" element={<Screener />} />
            <Route path="/stocks/templates" element={<StocksPortal />} />
            <Route path="/stocks/market" element={<MarketContext />} />
            <Route path="/stocks/strategies" element={<StrategiesHub />} />
            <Route path="/stocks/news/:section" element={<StockNewsSection />} />
            <Route path="/stocks/snapshot/:symbol" element={<SnapshotPreview />} />
            <Route path="/stocks/classification/:symbol" element={<ClassificationPreview />} />
            <Route path="/stocks/:symbol" element={<StockDetail />} />

            {/* Articles Module - Public Access */}
            <Route path="/articles" element={<ArticlesPortal />} />
            <Route path="/articles/:id" element={<ArticleViewer />} />
            <Route path="/articles/category/:category" element={<CategoryList />} />

            {/* Reports Module - Static HTML Reports */}
            <Route path="/reports" element={<ReportsPortal />} />
            <Route path="/reports/category/:category" element={<ReportsCategoryList />} />
            <Route path="/reports/section/:sectionNumber" element={<ReportsSectionList />} />

            {/* News Module - Unified News Portal */}
            <Route path="/news" element={<NewsPortal />} />
            <Route path="/news/section/:section" element={<NewsSectionList />} />

            {/* Trading Module - Signal-based Study Articles */}
            <Route path="/trading" element={<TradingPortal />} />
            <Route path="/trading/section/:section" element={<TradingSectionList />} />

            {/* History Module - Evergreen Financial History */}
            <Route path="/history" element={<HistoryPortal />} />
            <Route path="/history/section/:section" element={<HistorySectionList />} />
            <Route path="/history/collection/:collectionSlug" element={<HistoryCollectionPage />} />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" />} />

          </Routes>
          <Footer />
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
